import React from "react"
import Swal from "sweetalert2"
import { toast } from "react-toastify"
import MyModal from "@common-utils/MyModal"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyGoogleDriveFileViewerInModal from "../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import {
     documentsEmbaucheUploadRequest,
     documentsEmbaucheReplaceRequest,
     documentsEmbaucheRenameRequest,
     documentsEmbaucheDeleteRequest,
     DOCUMENT_EMBAUCHE_MAX_SIZE,
     IDocumentsEmbaucheStatutModel,
     IDocumentEmbaucheFileModel,
} from "./_requests"

const ACCEPT = "application/pdf, image/*, .ppt, .pptx, .xls, .xlsx, .doc, .docx"

const formatSize = (bytes: number | null): string => {
     if (bytes == null) return ""
     if (bytes < 1024) return `${bytes} o`
     if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`
     return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
}

// Nom de fichier sans son extension (pour pré-remplir le titre).
const stripExtension = (name: string): string => name.replace(/\.[^/.]+$/, "")

type StagedFile = { key: number; file: File; title: string }

type Props = {
     entityKey: string
     statut: IDocumentsEmbaucheStatutModel
     docQuery?: string // filtre d'affichage des documents (recherche par nom)
}

// Un dossier « statut » (ETAM ou CADRE) d'une entité : liste consultable, ajout multi-fichiers (titre par fichier),
// renommage, remplacement, suppression logique.
const StatutFolder = ({ entityKey, statut, docQuery }: Props) => {
     const [documents, setDocuments] = React.useState<IDocumentEmbaucheFileModel[]>(statut.documents)
     const [busy, setBusy] = React.useState<boolean>(false)
     const [activeDoc, setActiveDoc] = React.useState<IDocumentEmbaucheFileModel | null>(null)

     // Modale d'ajout (multi-fichiers)
     const [showAdd, setShowAdd] = React.useState<boolean>(false)
     const [staged, setStaged] = React.useState<StagedFile[]>([])
     const stagedKey = React.useRef<number>(0)

     // Modale de renommage
     const [renaming, setRenaming] = React.useState<IDocumentEmbaucheFileModel | null>(null)
     const [renameTitle, setRenameTitle] = React.useState<string>("")

     // Remplacement : input file caché par document + id du document en cours de remplacement (spinner ciblé)
     const replaceInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({})
     const [replacingId, setReplacingId] = React.useState<string | null>(null)

     const sortDocs = (docs: IDocumentEmbaucheFileModel[]) => [...docs].sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""))

     const shownDocuments = React.useMemo(() => {
          const q = (docQuery ?? "").trim().toLowerCase()
          if (!q) return documents
          return documents.filter(d => (d.title ?? "").toLowerCase().includes(q) || (d.fileName ?? "").toLowerCase().includes(q))
     }, [documents, docQuery])

     // ---- Ajout multi-fichiers -------------------------------------------------------------------
     function addFilesToStage(files: File[]) {
          if (!files.length) return
          setStaged(prev => [...prev, ...files.map(file => ({ key: stagedKey.current++, file, title: stripExtension(file.name) }))])
     }

     function resetAdd() {
          setShowAdd(false)
          setStaged([])
     }

     function handleAddAll() {
          const oversize = staged.filter(s => s.file.size > DOCUMENT_EMBAUCHE_MAX_SIZE)
          if (oversize.length) {
               toast.error(`${oversize.length} fichier(s) dépassent 2 Mo. Retirez-les avant d'ajouter.`, { autoClose: false })
               return
          }
          const missingTitle = staged.some(s => !s.title.trim())
          if (missingTitle) {
               toast.error("Chaque document doit avoir un titre.")
               return
          }
          if (!staged.length) return

          setBusy(true)
          ;(async () => {
               const added: IDocumentEmbaucheFileModel[] = []
               let failures = 0
               for (const s of staged) {
                    try {
                         const r = await documentsEmbaucheUploadRequest(entityKey, statut.statut, s.title.trim(), s.file)
                         added.push(r.data)
                    } catch {
                         failures++
                    }
               }
               if (added.length) {
                    setDocuments(prev => sortDocs([...prev, ...added]))
                    toast.success(`${added.length} document(s) ajouté(s).`)
               }
               if (failures) toast.error(`${failures} document(s) n'ont pas pu être envoyés.`, { autoClose: false })
               setBusy(false)
               resetAdd()
          })()
     }

     // ---- Renommage ------------------------------------------------------------------------------
     function openRename(doc: IDocumentEmbaucheFileModel) {
          setRenaming(doc)
          setRenameTitle(doc.title ?? doc.fileName ?? "")
     }

     function handleRename() {
          if (!renaming) return
          const t = renameTitle.trim()
          if (!t) {
               toast.error("Le titre est requis.")
               return
          }
          setBusy(true)
          documentsEmbaucheRenameRequest(entityKey, statut.statut, renaming.id, t)
               .then(r => {
                    setDocuments(prev => sortDocs(prev.map(d => (d.id === r.data.id ? r.data : d))))
                    toast.success("Titre modifié.")
                    setRenaming(null)
               })
               .catch(e => toast.error(e?.response?.data?.detail ?? "Échec du renommage.", { autoClose: false }))
               .finally(() => setBusy(false))
     }

     // ---- Remplacement ---------------------------------------------------------------------------
     function replace(doc: IDocumentEmbaucheFileModel, file: File) {
          if (file.size > DOCUMENT_EMBAUCHE_MAX_SIZE) {
               toast.error(`« ${file.name} » dépasse la taille maximale autorisée (2 Mo).`, { autoClose: false })
               return
          }
          setBusy(true)
          setReplacingId(doc.id)
          documentsEmbaucheReplaceRequest(entityKey, statut.statut, doc.id, file)
               .then(r => {
                    setDocuments(prev => sortDocs([...prev.filter(d => d.id !== doc.id), r.data]))
                    toast.success("Document remplacé.")
               })
               .catch(e => toast.error(e?.response?.data?.detail ?? "Échec du remplacement.", { autoClose: false }))
               .finally(() => {
                    setBusy(false)
                    setReplacingId(null)
               })
     }

     // ---- Suppression logique --------------------------------------------------------------------
     function remove(doc: IDocumentEmbaucheFileModel) {
          Swal.fire({
               icon: "warning",
               title: "Supprimer définitivement ?",
               html: `<b>${doc.title ?? "Document"}</b> sera <b>définitivement supprimé du Drive</b>.<br/>Cette action est <b>irréversible</b>.`,
               showCancelButton: true,
               confirmButtonText: "Supprimer définitivement",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#f1416c",
               showLoaderOnConfirm: true,
               allowOutsideClick: () => !Swal.isLoading(),
               allowEscapeKey: () => !Swal.isLoading(),
               preConfirm: () =>
                    documentsEmbaucheDeleteRequest(entityKey, statut.statut, doc.id)
                         .then(() => setDocuments(prev => prev.filter(d => d.id !== doc.id)))
                         .catch(err => Swal.showValidationMessage(err?.response?.data?.detail ?? "Échec de la suppression.")),
          })
     }

     if (!statut.configured) {
          return (
               <div className="alert alert-light-warning d-flex align-items-center mb-0">
                    <i className="fas fa-triangle-exclamation me-2" /> Aucun dossier Drive configuré pour ce statut.
               </div>
          )
     }

     return (
          <div>
               <div className="d-flex flex-column gap-2 mb-3">
                    {documents.length === 0 && <div className="text-muted fs-7">Aucun document.</div>}
                    {documents.length > 0 && shownDocuments.length === 0 && <div className="text-muted fs-7">Aucun document ne correspond à la recherche.</div>}
                    {shownDocuments.map(doc => {
                         const title = doc.title ?? doc.fileName ?? "Document"
                         const showFileName = doc.fileName && doc.fileName !== title
                         return (
                              <div key={doc.id} className="d-flex align-items-center gap-3 border border-dashed border-gray-300 rounded px-3 py-2">
                                   <i className="fas fa-file-pdf fs-4 text-danger flex-shrink-0" />
                                   <div className="min-w-0 flex-grow-1 cursor-pointer" onClick={() => setActiveDoc(doc)} title="Cliquez pour visualiser">
                                        <div className="fw-semibold text-gray-800 text-truncate">{title}</div>
                                        <div className="text-muted fs-8 text-truncate">
                                             {showFileName ? <><i className="fas fa-paperclip me-1" />{doc.fileName}</> : null}
                                             {showFileName && doc.size != null ? " · " : null}
                                             {doc.size != null ? formatSize(doc.size) : null}
                                        </div>
                                   </div>
                                   <button type="button" className="btn btn-sm btn-icon btn-light-primary flex-shrink-0" title="Voir" onClick={() => setActiveDoc(doc)}>
                                        <i className="fas fa-eye" />
                                   </button>
                                   <button type="button" className="btn btn-sm btn-icon btn-light-info flex-shrink-0" title="Renommer" disabled={busy} onClick={() => openRename(doc)}>
                                        <i className="fas fa-pen" />
                                   </button>
                                   <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-light-warning flex-shrink-0"
                                        title="Remplacer le fichier (même titre)"
                                        disabled={busy}
                                        onClick={() => replaceInputRefs.current[doc.id]?.click()}
                                   >
                                        {replacingId === doc.id ? <MySimpleSpinner size={"sm"} /> : <i className="fas fa-arrows-rotate" />}
                                   </button>
                                   <button type="button" className="btn btn-sm btn-icon btn-light-danger flex-shrink-0" title="Supprimer" disabled={busy} onClick={() => remove(doc)}>
                                        <i className="fas fa-trash" />
                                   </button>
                                   <input
                                        type="file"
                                        accept={ACCEPT}
                                        className="d-none"
                                        ref={el => {
                                             replaceInputRefs.current[doc.id] = el
                                        }}
                                        onChange={e => {
                                             const f = e.target.files?.[0]
                                             if (f) replace(doc, f)
                                             e.target.value = ""
                                        }}
                                   />
                              </div>
                         )
                    })}
               </div>

               <button type="button" className="btn btn-sm btn-light-primary" disabled={busy} onClick={() => setShowAdd(true)}>
                    <i className="fas fa-plus me-1" /> Ajouter des documents
               </button>

               {/* Modale d'ajout multi-fichiers */}
               {showAdd && (
                    <MyModal title={`Ajouter des documents — ${statut.statutLabel}`} show={true} size={"lg"} onCloseCallback={() => !busy && resetAdd()}>
                         <div>
                              {/* Zone de dépôt */}
                              <label className="d-flex flex-column align-items-center justify-content-center text-center rounded p-6 mb-4 cursor-pointer border border-dashed border-gray-400 bg-light-primary">
                                   <i className="fas fa-cloud-arrow-up fs-2x text-primary mb-3" />
                                   <div className="fw-bold text-gray-800">Glissez-déposez ou cliquez pour choisir des fichiers</div>
                                   <div className="text-muted fs-8 mt-1">Un ou plusieurs fichiers · 2 Mo maximum par fichier</div>
                                   <input
                                        type="file"
                                        accept={ACCEPT}
                                        multiple
                                        className="d-none"
                                        onChange={e => {
                                             addFilesToStage(Array.from(e.target.files ?? []))
                                             e.target.value = ""
                                        }}
                                   />
                              </label>

                              {staged.length === 0 ? (
                                   <div className="text-muted fs-7 text-center mb-2">Aucun fichier sélectionné.</div>
                              ) : (
                                   <div className="d-flex flex-column gap-3 mb-2">
                                        {staged.map(s => {
                                             const oversize = s.file.size > DOCUMENT_EMBAUCHE_MAX_SIZE
                                             return (
                                                  <div key={s.key} className={`d-flex align-items-center gap-3 rounded p-3 border ${oversize ? "border-danger bg-light-danger" : "border-gray-300"}`}>
                                                       <i className={`fas fa-file fs-3 ${oversize ? "text-danger" : "text-primary"} flex-shrink-0`} />
                                                       <div className="flex-grow-1 min-w-0">
                                                            <input
                                                                 type="text"
                                                                 className="form-control form-control-sm mb-1"
                                                                 placeholder="Titre du document"
                                                                 value={s.title}
                                                                 onChange={e => setStaged(prev => prev.map(x => (x.key === s.key ? { ...x, title: e.target.value } : x)))}
                                                            />
                                                            <div className={`fs-8 text-truncate ${oversize ? "text-danger fw-bold" : "text-muted"}`}>
                                                                 <i className="fas fa-paperclip me-1" />
                                                                 {s.file.name} · {formatSize(s.file.size)}
                                                                 {oversize ? " — dépasse 2 Mo" : ""}
                                                            </div>
                                                       </div>
                                                       <button
                                                            type="button"
                                                            className="btn btn-sm btn-icon btn-light-danger flex-shrink-0"
                                                            title="Retirer"
                                                            disabled={busy}
                                                            onClick={() => setStaged(prev => prev.filter(x => x.key !== s.key))}
                                                       >
                                                            <i className="fas fa-xmark" />
                                                       </button>
                                                  </div>
                                             )
                                        })}
                                   </div>
                              )}

                              <div className="d-flex justify-content-end gap-2 mt-6">
                                   <button type="button" className="btn btn-sm btn-light" disabled={busy} onClick={resetAdd}>
                                        Annuler
                                   </button>
                                   <button
                                        type="button"
                                        className="btn btn-sm btn-primary d-inline-flex align-items-center"
                                        disabled={busy || staged.length === 0}
                                        onClick={handleAddAll}
                                   >
                                        {busy ? <MySimpleSpinner size={"sm"} /> : <><i className="fas fa-upload me-1" /> Ajouter {staged.length > 0 ? `(${staged.length})` : ""}</>}
                                   </button>
                              </div>
                         </div>
                    </MyModal>
               )}

               {/* Modale de renommage */}
               {renaming && (
                    <MyModal title={"Renommer le document"} show={true} onCloseCallback={() => !busy && setRenaming(null)}>
                         <div>
                              <label className="form-label required">Titre du document</label>
                              <input
                                   type="text"
                                   className="form-control form-control-sm mb-2"
                                   value={renameTitle}
                                   onChange={e => setRenameTitle(e.target.value)}
                                   onKeyDown={e => e.key === "Enter" && handleRename()}
                                   autoFocus
                              />
                              {renaming.fileName && <div className="text-muted fs-8 mb-4"><i className="fas fa-paperclip me-1" />{renaming.fileName}</div>}
                              <div className="d-flex justify-content-end gap-2 mt-4">
                                   <button type="button" className="btn btn-sm btn-light" disabled={busy} onClick={() => setRenaming(null)}>
                                        Annuler
                                   </button>
                                   <button type="button" className="btn btn-sm btn-primary d-inline-flex align-items-center" disabled={busy} onClick={handleRename}>
                                        {busy ? <MySimpleSpinner size={"sm"} /> : <><i className="fas fa-check me-1" /> Enregistrer</>}
                                   </button>
                              </div>
                         </div>
                    </MyModal>
               )}

               {activeDoc && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={activeDoc.id}
                         googleDriveMimetype={activeDoc.mimeType ?? ""}
                         modalTitle={<>{activeDoc.title ?? "Document"}</>}
                         handleCloseModal={() => setActiveDoc(null)}
                    />
               )}
          </div>
     )
}

export default StatutFolder
